import type { Container } from "../core/Container.js";
import type { Control } from "../core/Control.js";
import type { Desktop } from "../core/Desktop.js";
import type { Theme } from "../themes/Theme.js";
import { listNames, MarkupError, type WarningSink } from "./errors.js";
import type { Length } from "./values.js";
import type { VariableStore, VariableValue } from "./variables.js";
import type { XmlElement } from "./xml.js";

/**
 * What an attribute's text means.
 *
 * A list of strings is an enum, validated against exactly those spellings. Everything else names
 * one of the readers in `values.ts`. Declaring the type rather than writing a coercion per
 * attribute is what keeps the element table readable — a control's whole vocabulary fits on a
 * screen and reads like documentation, which is the only way it stays right as controls change.
 */
export type AttrType =
  | "string"
  | "text"
  | "number"
  | "boolean"
  | "length"
  | "insets"
  | "size"
  | "color"
  | "colorRef"
  | "font"
  | "list"
  | "anchor"
  | "tracks"
  | "date"
  | readonly string[];

export type AttrTable = Record<string, AttrType>;

/** Attributes already resolved to values, ready to hand to a constructor. */
export type Attrs = Record<string, unknown>;

/** What a handler named by an `on*` attribute is called with. */
export type MarkupHandler = (args: never, control: Control, view: MarkupViewLike) => void;

/**
 * What a handler and an element definition see of the loaded document.
 *
 * The same surface `MarkupView` offers, minus the lifecycle — a handler has no business showing
 * or disposing the document it is a part of. Declared here rather than imported from the loader
 * so the registry stays free of it, which is what lets an element definition live anywhere.
 */
export interface MarkupViewLike {
  readonly root: Control;
  get<T extends Control = Control>(name: string): T;
  tryGet<T extends Control = Control>(name: string): T | null;
  spec<T = unknown>(name: string): T;
  names(): string[];
  /** Set a declared variable; every property linked to it follows. `immediate` skips any easing. */
  set(name: string, value: VariableValue, immediate?: boolean): void;
}

/** Everything a `create` or `addChild` needs that is not in the element itself. */
export interface BuildContext {
  theme: Theme;
  desktop: Desktop | null;
  view: MarkupViewLike;
  warn: WarningSink;
  /** Register a named non-control — a menu item, a status panel — for `view.spec`. */
  nameSpec(name: string, spec: unknown): void;
  /** Build a child element into a control, applying every universal rule. */
  build(element: XmlElement, parent: Control | null): Control;
  /** Resolve one element's attributes against a table, without building anything. */
  attributesOf(element: XmlElement, table: AttrTable): Attrs;
  /** Look up a handler by the name an `on*` attribute gave, failing loudly if it is missing. */
  handler(name: string, at: XmlElement): MarkupHandler;
  /**
   * Resolve a length to pixels now, and again whenever the theme changes.
   *
   * `em` is the theme's font size, so a size written in it is a function of the theme rather than
   * a number fixed at load. Anything that stores pixels somewhere the loader cannot see — a
   * status panel's width, a column's width — goes through this instead of `toPixels`, or it
   * would be the one measurement in the document that a theme swap left behind.
   */
  bindEm(length: Length, apply: (pixels: number) => void): number;
  /**
   * Run `apply` against the theme now, and again whenever the theme changes.
   *
   * For anything resolved out of the palette or the metrics and then stored where the loader
   * cannot see it - a flare's color is the case that needed it.
   */
  bindTheme(apply: (theme: Theme) => void): void;
  /** The document's variables, which `<Variables>` declares into and `<Link>` links against. */
  variables: VariableStore;
  /**
   * Run `fn` once the document is attached to a desktop and laid out - after every element's own
   * `afterAttach`. Never run for a document built without a desktop.
   */
  later(fn: () => void): void;
}

/**
 * Which set of universal attributes an element takes.
 *
 * Every element takes the `control` set. The others add what their base class adds, so the table
 * for `Grid` says what a grid has that a panel does not, and nothing more.
 */
export type ElementBase = "control" | "container" | "panel" | "layout";

export interface ElementDefinition<T extends Control = Control> {
  tag: string;
  aliases?: readonly string[];
  base?: ElementBase;
  attributes?: AttrTable;
  create(attrs: Attrs, ctx: BuildContext, element: XmlElement): T;

  /** Where the element's text content goes. Defaults to the `text` option. */
  text?: (control: T, text: string, ctx: BuildContext) => void;

  /**
   * True when a child with no placement of its own should dock `fill`.
   *
   * The single most useful default in the dialect: `<Window><VStack>…</VStack></Window>` needs no
   * attributes at all, because a container with one child almost always means "fill me". Panels
   * that scroll say no — content in a scroller keeps its own size, or there is nothing to scroll.
   */
  docksChildren?: boolean;

  /**
   * Takes every child element that is not a decoration, instead of each being built as a control -
   * for an element whose children are a language of their own, as `<Art>`'s drawing is.
   */
  content?(control: T, children: readonly XmlElement[], ctx: BuildContext): void;

  /** Where a built child control goes. Defaults to `parent.controls.add`. */
  addChild?(parent: T, child: Control, element: XmlElement, ctx: BuildContext): void;

  /**
   * Child elements that are data rather than controls — `<Item>`, `<Node>`, `<Tab>`.
   *
   * Resolved before the global table, so the same tag can mean different things inside different
   * parents: an `<Item>` in a menu is a `MenuItemSpec`, in a status bar a `StatusPanelSpec`, and
   * in a list box a string. That is what keeps the markup reading like the thing it describes
   * rather than like the class names underneath it.
   */
  childSpecs?: Record<string, (element: XmlElement, ctx: BuildContext, parent: T) => void>;

  /**
   * Applied after the whole tree is attached to a desktop.
   *
   * For everything that cannot be known until then: a splitter position is a fraction of a width
   * the container does not have until its parent has laid it out, and a window cannot be centered
   * in a work area it has not joined.
   */
  afterAttach?(control: T, attrs: Attrs, ctx: BuildContext): void;
}

export class ElementRegistry {
  #byTag = new Map<string, ElementDefinition<never>>();

  /**
   * Register an element, replacing any with the same tag.
   *
   * Replacing rather than refusing, the way `registerFlare` does: an application that wants
   * `<Button>` to mean its own button subclass should be able to say so in one line, and a
   * reload during development should not need a fresh registry.
   */
  register<T extends Control>(definition: ElementDefinition<T>): void {
    const stored = definition as unknown as ElementDefinition<never>;
    this.#byTag.set(definition.tag, stored);
    for (const alias of definition.aliases ?? []) this.#byTag.set(alias, stored);
  }

  lookup(tag: string): ElementDefinition<never> | undefined {
    return this.#byTag.get(tag);
  }

  /** Every tag and alias that can be written, for an error that has to suggest something. */
  tags(): string[] {
    return [...this.#byTag.keys()].sort();
  }

  require(element: XmlElement): ElementDefinition<never> {
    const found = this.#byTag.get(element.name);
    if (found) return found;
    throw new MarkupError(
      `there is no <${element.name}> element; try ${listNames(this.tags())}`,
      element,
    );
  }
}

/** The registry `loadMarkup` uses when it is not given another. */
export const elements = new ElementRegistry();

export function registerElement<T extends Control>(definition: ElementDefinition<T>): void {
  elements.register(definition);
}

export function elementTags(): string[] {
  return elements.tags();
}

/** The common case: a container that adds children straight into `controls`. */
export function addToControls(parent: Control, child: Control): void {
  (parent as Container).controls.add(child);
}
